import * as Yup from 'yup';
import { IUserUpsert } from './userUpsert';
import * as uuid from 'uuid';
import { IAddUserFormValues } from './addUserFormValues';
import EmailHelper from '../../../../services/emailHelper';
import { ISqlEmployee } from './sqlEmployee';
import { IEmployee } from '../../../employee';
import moment from 'moment';

export default class UserModelHelper {
    public static createAddUserFormValues = () : IAddUserFormValues => {
        return {
            guid: uuid.v4(),
            name: '',
            email: '',
            employeeNumber: '', 
            password: '',
            isActive: true,
        };
    };

    public static createAddUserUpsert = (addUserFormValues : IAddUserFormValues) : IUserUpsert => {
        return {
            id: 0,
            uid: null,
            guid: addUserFormValues.guid,
            name: addUserFormValues.name,
            password: addUserFormValues.password,
            email: addUserFormValues.email,
            employeeNumber: addUserFormValues.employeeNumber.toUpperCase(),
            areaNames: [],
            damConstructionTypeIds: [],
            isActive: addUserFormValues.isActive,

            isBoreholeAdmin: false,
            isBoreholeExport: false,
            isBoreholeCreateAdmin: false,
            isBoreholeUser: false,
            isBoreholeTester: false,
            isBoreholeYieldAdjuster: false,
            isBoreholeFlowMeter: false,
            isTransformerAdmin: false,
            isTransformerUser: false,
            isTransformerExport: false,
            isTransformerLpuTester: false,
            isTransformerSpuTester: false,
            isTransformerLpuTariff: false,
            IsTransformerSpuTariff: false,
            isWeatherStationAdmin: false,
            isWeatherStationUser: false,
            isWeatherStationTester: false,
            isWeatherExport: false,
            isRiverAdmin: false,
            isRiverUser: false,
            isRiverTester: false,
            isRiverExport: false,
            
            isEmployeeAdmin: false,
            isMasterDataAdmin: false,
            isDivisionAdmin: false,
            isBoreholeMasterDataAdmin: false,
            isTransformerTariffAdmin: false,
            isTransformerBudgetAdmin: false,
            isTransformerMasterDataAdmin: false,
            isDamAdmin: false,
            isDamUser: false,
            isDamTester: false,
            isDamRegReportUser: false,
            isDamSafetyReportUser: false,
            isDamExport: false,
            isPumpingStationAdmin: false,
            isPumpingStationCreateAdmin: false,
            isPumpingStationUser: false,
            isPumpingStationTester: false,
            isPumpingStationExport: false,
            isCompanyAdmin: false,
            isTransformerMunicipalityTester: false,
            isTransformerPrepaidTester: false,
            isMunicipalityInvoice: false,
            isSPSInvoice: false,
            isFlowmeterAdmin: false,
            isFlowmeterExport: false,
            isFlowmeterCreateAdmin: false,
            isFlowmeterUser: false,
            isFlowmeterTester: false,
            isFillingPointAdmin: false,
            isFillingPointUser: false,
            isFillingPointTester: false,
            isRainMeterUser: false,
            isRainMeterMeasurementUser: false,
            isRainMeterAdmin: false,
            isValueAddedTaxAdmin: false,
            isGroundWaterZoneAdmin: false,
            isDevelopmentToolsAdmin : false,
        };
    };

    // Validation schema used by formik. https://github.com/jquense/yup
    public static addUserFormSchema = () : Yup.ObjectSchema<IAddUserFormValues> => Yup.object({
        guid: Yup.string().required('Required'),
        name: Yup.string().required('Required'),
        email: Yup.string()
            .email('Invalid email format')
            .test('Valid Domain', 'Invalid Email Domain', (value) => {
                if (value == null) return true;
                return EmailHelper.validateDomain(value);
            })
            .required('Email is required'),
        employeeNumber: Yup.string().required('Required'),
        password: Yup.string().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });

    public static fromSqlToFirebaseEmployee = (employee : ISqlEmployee) : IEmployee => {
        const firebaseEmployee : IEmployee = {
            Id: employee.uid,
            Name: employee.name ?? '',
            Email: employee.email ?? '',
            EmployeeNumber: employee.employeeNumber ?? '',
            IsActive: employee.isActive,
            Areas: employee.areas.map((x) => x.name),
            DamConstructionTypes: employee.damConstructionTypeIds,
            PhotoURL: employee.profilePictureURL,

            IsBoreholeAdmin: employee.isBoreholeAdmin,
            IsBoreholeExport: employee.isBoreholeExport,
            IsBoreholeCreateAdmin: employee.isBoreholeCreateAdmin,
            IsBoreholeUser: employee.isBoreholeUser,
            IsBoreholeTester: employee.isBoreholeTester,
            IsBoreholeYieldAdjuster: employee.isBoreholeYieldAdjuster,
            IsBoreholeFlowMeter: employee.isBoreholeFlowMeter,
            IsTransformerAdmin: employee.isTransformerAdmin,
            IsTransformerUser: employee.isTransformerUser,
            IsTransformerExport: employee.isTransformerExport,
            IsTransformerLpuTester: employee.isTransformerLpuTester,
            IsTransformerSpuTester: employee.isTransformerSpuTester,
            IsTransformerLpuTariff: employee.isTransformerLpuTariff,
            IsTransformerSpuTariff: employee.isTransformerSpuTariff,
            IsWeatherStationAdmin: employee.isWeatherStationAdmin,
            IsWeatherStationUser: employee.isWeatherStationUser,
            IsWeatherStationTester: employee.isWeatherStationTester,
            IsWeatherExport: employee.isWeatherExport,
            IsRiverAdmin: employee.isRiverAdmin,
            IsRiverUser: employee.isRiverUser,
            IsRiverTester: employee.isRiverTester,
            IsRiverExport: employee.isRiverExport,
            IsEmployeeAdmin: employee.isEmployeeAdmin,
            IsMasterDataAdmin: employee.isMasterDataAdmin,
            IsDivisionAdmin: employee.isDivisionAdmin,
            IsBoreholeMasterDataAdmin: employee.isBoreholeMasterDataAdmin,
            IsTransformerTariffAdmin: employee.isTransformerTariffAdmin,
            IsTransformerBudgetAdmin: employee.isTransformerBudgetAdmin,
            IsTransformerMasterDataAdmin: employee.isTransformerMasterDataAdmin,
            IsDamAdmin: employee.isDamAdmin,
            IsDamUser: employee.isDamUser,
            IsDamTester: employee.isDamTester,
            IsDamRegReportUser: employee.isDamRegReportUser,
            IsDamSafetyReportUser: employee.isDamSafetyReportUser,
            IsDamExport: employee.isDamExport,
            IsPumpingStationAdmin: employee.isPumpingStationAdmin,
            IsPumpingStationCreateAdmin: employee.isPumpingStationCreateAdmin,
            IsPumpingStationUser: employee.isPumpingStationUser,
            IsPumpingStationTester: employee.isPumpingStationTester,
            IsPumpingStationExport: employee.isPumpingStationExport,
            IsCompanyAdmin: employee.isCompanyAdmin,
            IsTransformerMunicipalityTester: employee.isTransformerMunicipalityTester,
            IsTransformerPrepaidTester: employee.isTransformerPrepaidTester,
            IsMunicipalityInvoice: employee.isMunicipalityInvoice,
            IsSPSInvoice: employee.isSPSInvoice,
            IsFlowmeterAdmin: employee.isFlowmeterAdmin,
            IsFlowmeterExport: employee.isFlowmeterExport,
            IsFlowmeterCreateAdmin: employee.isFlowmeterCreateAdmin,
            IsFlowmeterUser: employee.isFlowmeterUser,
            IsFlowmeterTester: employee.isFlowmeterTester,
            IsFillingPointAdmin: employee.isFillingPointAdmin,
            IsFillingPointUser: employee.isFillingPointUser,
            IsFillingPointTester: employee.isFillingPointTester,
            IsRainMeterUser: employee.isRainMeterUser,
            IsRainMeterMeasurementUser: employee.isRainMeterMeasurementUser,
            IsRainMeterAdmin: employee.isRainMeterAdmin,
            IsValueAddedTaxAdmin: employee.isValueAddedTaxAdmin,
            IsGroundWaterZoneAdmin: employee.isGroundWaterZoneAdmin,
            IsDevelopmentToolsAdmin: employee.isDevelopmentToolsAdmin,
        };

        return firebaseEmployee;
    };

    public static fromFirebaseToSqlEmployee = (employee : IEmployee) : ISqlEmployee => {
        return {
            id: 0,
            uid: employee.Id,
            guid: uuid.v4(),
            name: employee.Name,
            email: employee.Email ?? '',
            employeeNumber: employee.EmployeeNumber ?? '',
            areas: [],
            damConstructionTypes: [],
            damConstructionTypeIds: employee.DamConstructionTypes,
            isActive: employee.IsActive ?? false,
            lastSignIn: null,
            profilePictureURL: employee.PhotoURL,
            createdById: 0,
            createdByName: '',
            createdOn: moment(),
            updatedById: 0,
            updatedByName: '',
            updatedOn: moment(),

            isBoreholeAdmin: employee.IsBoreholeAdmin ?? false,
            isBoreholeExport: employee.IsBoreholeExport ?? false,
            isBoreholeCreateAdmin: employee.IsBoreholeCreateAdmin ?? false,
            isBoreholeUser: employee.IsBoreholeUser ?? false,
            isBoreholeTester: employee.IsBoreholeTester ?? false,
            isBoreholeYieldAdjuster: employee.IsBoreholeYieldAdjuster ?? false,
            isBoreholeFlowMeter: employee.IsBoreholeFlowMeter ?? false,
            isTransformerAdmin: employee.IsTransformerAdmin ?? false,
            isTransformerUser: employee.IsTransformerUser ?? false,
            isTransformerExport: employee.IsTransformerExport ?? false,
            isTransformerLpuTester: employee.IsTransformerLpuTester ?? false,
            isTransformerSpuTester: employee.IsTransformerSpuTester ?? false,
            isTransformerLpuTariff: employee.IsTransformerLpuTariff ?? false,
            isTransformerSpuTariff: employee.IsTransformerSpuTariff ?? false,
            isWeatherStationAdmin: employee.IsWeatherStationAdmin ?? false,
            isWeatherStationUser: employee.IsWeatherStationUser ?? false,
            isWeatherStationTester: employee.IsWeatherStationTester ?? false,
            isWeatherExport: employee.IsWeatherExport ?? false,
            isRiverAdmin: employee.IsRiverAdmin ?? false,
            isRiverUser: employee.IsRiverUser ?? false,
            isRiverTester: employee.IsRiverTester ?? false,
            isRiverExport: employee.IsRiverExport ?? false,
            isEmployeeAdmin: employee.IsEmployeeAdmin ?? false,
            isMasterDataAdmin: employee.IsMasterDataAdmin ?? false,
            isDivisionAdmin: employee.IsDivisionAdmin ?? false,
            isBoreholeMasterDataAdmin: employee.IsBoreholeMasterDataAdmin ?? false,
            isTransformerTariffAdmin: employee.IsTransformerTariffAdmin ?? false,
            isTransformerBudgetAdmin: employee.IsTransformerBudgetAdmin ?? false,
            isTransformerMasterDataAdmin: employee.IsTransformerMasterDataAdmin ?? false,
            isDamAdmin: employee.IsDamAdmin ?? false,
            isDamUser: employee.IsDamUser ?? false,
            isDamTester: employee.IsDamTester ?? false,
            isDamRegReportUser: employee.IsDamRegReportUser ?? false,
            isDamSafetyReportUser: employee.IsDamSafetyReportUser ?? false,
            isDamExport: employee.IsDamExport ?? false,
            isPumpingStationAdmin: employee.IsPumpingStationAdmin ?? false,
            isPumpingStationCreateAdmin: employee.IsPumpingStationCreateAdmin ?? false,
            isPumpingStationUser: employee.IsPumpingStationUser ?? false,
            isPumpingStationTester: employee.IsPumpingStationTester ?? false,
            isPumpingStationExport: employee.IsPumpingStationExport ?? false,
            isCompanyAdmin: employee.IsCompanyAdmin ?? false,
            isTransformerMunicipalityTester: employee.IsTransformerMunicipalityTester ?? false,
            isTransformerPrepaidTester: employee.IsTransformerPrepaidTester ?? false,
            isMunicipalityInvoice: employee.IsMunicipalityInvoice ?? false,
            isSPSInvoice: employee.IsSPSInvoice ?? false,
            isFlowmeterAdmin: employee.IsFlowmeterAdmin ?? false,
            isFlowmeterExport: employee.IsFlowmeterExport ?? false,
            isFlowmeterCreateAdmin: employee.IsFlowmeterCreateAdmin ?? false,
            isFlowmeterUser: employee.IsFlowmeterUser ?? false,
            isFlowmeterTester: employee.IsFlowmeterTester ?? false,
            isFillingPointAdmin: employee.IsFillingPointAdmin ?? false,
            isFillingPointUser: employee.IsFillingPointUser ?? false,
            isFillingPointTester: employee.IsFillingPointTester ?? false,
            isRainMeterUser: employee.IsRainMeterUser ?? false,
            isRainMeterMeasurementUser: employee.IsRainMeterMeasurementUser ?? false,
            isRainMeterAdmin: employee.IsRainMeterAdmin ?? false,
            isValueAddedTaxAdmin: employee.IsValueAddedTaxAdmin ?? false,
            isGroundWaterZoneAdmin: employee.IsGroundWaterZoneAdmin ?? false,
            isDevelopmentToolsAdmin: employee.IsDevelopmentToolsAdmin ?? false,
        };
    };
}
